import { useState } from "react";
import { navigation } from "../data/content";
import { siteConfig } from "../config/site";
import { imageSize } from "../data/imageSizes";

const logoSize = imageSize("/my-little-classic-logo.png");

// 배포된 실제 주소는 /about/ 형태입니다. 내부 링크를 최종 주소로 내보내면
// 크롤러가 301을 거치지 않고 바로 페이지에 도달합니다.
export function canonicalHref(href) {
  const [, path, rest = ""] = href.match(/^([^?#]*)([?#].*)?$/);
  if (!path.startsWith("/")) return href;
  return path === "/" ? `/${rest}` : `${path.replace(/\/$/, "")}/${rest}`;
}

export function returnToPreviousPage(event, expectedPath) {
  const from = window.history.state?.from;
  if (typeof from !== "string") return;
  const fromPath = new URL(from, window.location.origin).pathname.replace(/\/$/, "") || "/";
  if (fromPath !== expectedPath) return;
  event.preventDefault();
  window.history.back();
}

export function Link({ href, navigate, children, ...props }) {
  const internal = href.startsWith("/") || href.startsWith("#");
  const finalHref = href.startsWith("/") ? canonicalHref(href) : href;
  return <a href={finalHref} {...props} onClick={(event) => {
    props.onClick?.(event);
    if (!event.defaultPrevented && internal && navigate) { event.preventDefault(); navigate(finalHref); }
  }}>{children}</a>;
}

export function Layout({ path, navigate, children }) {
  const [open, setOpen] = useState(false);
  const c = siteConfig.contact;
  const hasPhone = c.phone !== "CONTACT_PHONE";
  const hasEmail = c.email !== "CONTACT_EMAIL";
  const hasAddress = c.address !== "VENUE_ADDRESS";
  const hasBusinessInfo = c.businessInfo !== "BUSINESS_INFO";
  return <>
    <header className="site-header">
      <Link href="/" navigate={navigate} className="brand" aria-label="마이리틀클래식 홈" onClick={() => setOpen(false)}><img src="/my-little-classic-logo.png" alt="마이리틀클래식" {...logoSize} decoding="async" /></Link>
      <button className="menu-button" type="button" aria-expanded={open} aria-controls="site-nav" onClick={() => setOpen(!open)}><span /><span /><span className="sr-only">{open ? "메뉴 닫기" : "메뉴 열기"}</span></button>
      <nav id="site-nav" className={open ? "open" : ""} aria-label="주요 메뉴">{navigation.map(([href, label]) => <Link key={href} href={href} navigate={navigate} className={path === href ? "active" : ""} aria-current={path === href ? "page" : undefined} onClick={() => setOpen(false)}>{label}</Link>)}</nav>
      <Link className="header-cta" href="/inquiry" navigate={navigate}>공연 문의 <span aria-hidden="true">↗</span></Link>
    </header>
    <NavContext.Provider value={navigate}>{children}</NavContext.Provider>
    <footer className="site-footer">
      <div><img src="/my-little-classic-logo.png" alt="마이리틀클래식" {...logoSize} loading="lazy" decoding="async" /><p>아이의 첫 클래식 경험을 설계합니다.</p></div>
      {(hasPhone || hasEmail || hasAddress) && <div><b>CONTACT</b><p>{hasPhone && <>전화 {c.phone}<br /></>}{hasEmail && <>이메일 {c.email}<br /></>}{hasAddress && <>주소 {c.address}</>}</p>{c.naverPlace && <p><a href={c.naverPlace} target="_blank" rel="noreferrer">네이버 지도에서 길찾기 ↗</a></p>}</div>}
      <div><b>GUIDE</b><p><Link href="/venue" navigate={navigate}>동탄 영유아 전용 공연장</Link><br /><Link href="/guide/field-trip-checklist" navigate={navigate}>어린이집 현장학습 장소 고르는 법</Link><br /><Link href="/guide/first-classic-age" navigate={navigate}>영유아 클래식 공연, 몇 살부터 괜찮을까</Link></p></div>
      <div><b>INFORMATION</b>{hasBusinessInfo && <p>사업자 정보 {c.businessInfo}</p>}<Link href="/privacy" navigate={navigate}>개인정보처리방침</Link></div>
      <p className="copyright">© MY LITTLE CLASSIC. ALL RIGHTS RESERVED.</p>
    </footer>
    <div className="mobile-actions" aria-label="빠른 문의">
      {hasPhone ? <a href={`tel:${c.phone.replace(/[^+\d]/g, "")}`}>전화</a> : <span aria-disabled="true">전화</span>}
      {c.kakao === "KAKAO_URL" ? <span aria-disabled="true">카카오톡</span> : <a href={c.kakao} target="_blank" rel="noreferrer">카카오톡</a>}
      <Link href="/inquiry" navigate={navigate}>문의</Link>
    </div>
  </>;
}

import { createContext, useContext } from "react";
const NavContext = createContext(null);
export const useNavigate = () => useContext(NavContext);
